import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'redux/store';
import styled from '@emotion/styled';
import { Box, Divider, Grid, IconButton, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';

import DetailItem from 'components/Staff/ItemDetail';
import Button from 'components/Common/Button';
import TextInput from 'components/Common/TextInput';
import Modal from 'components/Common/Modal';
import { dateFormat } from 'utils/helpers';
import MuiLoader from 'components/Common/Loader';
import { fetchParishMemberDetails, updateParishionerDetails } from 'redux/slices/community/parishMemberDetailSlice';

const InnerContainer = styled('div')({
    backgroundColor: 'white',
    paddingTop: '4vh',
    paddingBottom: 16,
    marginTop: 12,
});

const fontSize = { xs: 10, md: 16 };

function ParishonerUserDetailContainer() {
    const dispatch = useDispatch<AppDispatch>();
    const params = useParams();
    const [openModal, setOpenModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [newParishionerId, setNewParishionerId] = useState('');
    const { details, loading: parishionerDetailLoading } = useSelector((state: RootState) => state.community.parishionerDetails);
    const isMerchant = useSelector((state: RootState) => state?.auth?.isMerchant);
    const { name, email, username, createdDate, title, parishionerId, status, address } = details || {};
    const { city, state, postalcode: zip, address1 = '', address2 = '' } = address || {};
    const { uuid } = params || {};
    useEffect(() => {
        uuid && dispatch(fetchParishMemberDetails({ uuid }));
    }, [uuid, dispatch]);

    const updateParishionerId = async () => {
        console.log('Updating parish', newParishionerId);
        setLoading(true);
        const res = await dispatch(
            updateParishionerDetails({
                username,
                payload: {
                    parishionerId: newParishionerId,
                },
            })
        );
        setLoading(false);
        if (res?.payload?.code === 200) {
            uuid && dispatch(fetchParishMemberDetails({ uuid }));
            setOpenModal(false);
            setNewParishionerId('');
        }
    };
    const body = useMemo(() => {
        return (
            <>
                {status ? (
                    <>
                        <Grid item xs={12} md={6}>
                            <DetailItem label="Name" value={name} />
                            <DetailItem label="Email/user name" value={email || username} />
                            <DetailItem label="Status" value={title || status} />
                            <DetailItem label="Phone Number" value={dateFormat(createdDate)} />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <DetailItem label="Address" value={(address1 + ' ' + address2).trim()} />
                            <DetailItem label="City" value={city} />
                            <DetailItem label="State" value={state} />
                            <DetailItem label="Zip" value={zip} />
                        </Grid>
                    </>
                ) : (
                    <>
                        <Grid item xs={12} md={6}>
                            <DetailItem label="Name" value={name} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <DetailItem label="Email/user name" value={email || username} />
                        </Grid>
                    </>
                )}
            </>
        );
    }, [city, createdDate, details, email, name, state, status, title, username, zip]);
    return (
        <InnerContainer>
            <MuiLoader loading={parishionerDetailLoading} absolute />
            <Box mx={8} style={{ marginTop: 16 }}>
                {' '}
                {/* Add consistent padding */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        paddingLeft: 6,
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography sx={{ fontSize: 16, fontWeight: 400, my: 1 }}>Parishioner id: {parishionerId}</Typography>

                    {/* <IconButton onClick={() => setOpenModal(true)}>
                        <EditIcon />
                    </IconButton> */}
                </div>
                <Divider sx={{ backgroundColor: '#6B7280' }} />
                <Grid direction={{ md: 'column', lg: 'row' }} sx={{ my: 3 }} container spacing={2} justifyContent="center">
                    {body}
                </Grid>
                <TextInput
                    icon=""
                    placeholder="Type here"
                    width={'100%'}
                    label="Parishioner ID #"
                    value={newParishionerId}
                    onChange={(e) => setNewParishionerId(e.target.value)}
                    sx={{ my: 6 }}
                />
                <div style={{ marginTop: 74, display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
                    <Button
                        onClick={() => setOpenModal(true)}
                        disabled={!isMerchant}
                        sx={{ width: { xs: '100%', sm: '40%' } }}
                        title="Save"
                    />
                </div>
            </Box>

            <Modal isOpen={openModal} modalTitle="Edit parishioner ID" onClose={() => setOpenModal(false)}>
                <>
                    <Typography sx={{ fontSize, my: 2 }}>
                        This ID is to easily differentiate from other parishioners with the same or similar name or username. You may also
                        confirm this is a valid account that belongs to the parishioner attached to the account.
                        <br />
                    </Typography>

                    <Typography sx={{ textAlign: 'left', fontSize, my: 1 }}>Parishioner ID#:</Typography>
                    <TextInput value={newParishionerId} icon={null} width={'100%'} onChange={(e) => setNewParishionerId(e.target.value)} />
                    <div style={{ marginTop: 22, marginBottom: 22 }}>
                        <Typography sx={{ textAlign: 'center', fontSize }}>Is this a valid user account?</Typography>
                        <Typography sx={{ textAlign: 'center', fontSize, my: 1 }}>
                            <IconButton>
                                <CheckCircleIcon htmlColor="#202A44" sx={{ fontSize }} />
                            </IconButton>
                            Yes, I know this account is valid.
                        </Typography>
                    </div>

                    <Button loading={loading} onClick={updateParishionerId} width={'100%'} title="Save" />
                </>
            </Modal>
        </InnerContainer>
    );
}

export default ParishonerUserDetailContainer;
